import {merge} from 'lodash';
import Assessment from '../assessment';
import getTooLongSentences from '../../helpers/assessments/checkForTooLongSentences';
import formatNumber from '../../../helpers/formatNumber';
import AssessmentResult from '../../../values/AssessmentResult';
import {MAIN_CONTENT_POINTS, TEXT_SENTENCE_LENGTH_ID} from '@axyseo/const/analysis';

/**
 * Represents the assessment that will calculate the length of sentences in the text.
 */
class SentenceLengthInTextAssessment extends Assessment {
  /**
	 * Sets the identifier and the config.
	 *
	 * @param {object} config			The scoring configuration that should be used.

	 * @returns {void}
	 */
  constructor(config = {}) {
    super();

    const defaultConfig = {
      id: TEXT_SENTENCE_LENGTH_ID,
      priority: 'high',
      docUrl:
        'https://docs.avada.io/seo-suite-help-center/seo-audit/on-page-seo/checklist#sentence-length',
      ctaType: 'fix',
      fixPosition: 'sentenceLength',
      recommendedLength: 20,
      slightlyTooMany: 25,
      farTooMany: 30,
      title: 'Text Sentence Length',
      content: {
        improve: '',
        bad:
          'Sentence too long. Keep sentence length less than 20 words to improve readability and flow.',
        good: 'Sentence length is optimized for readability, less than 20 words.'
      }
    };

    this._config = merge(defaultConfig, config);
    this.identifier = TEXT_SENTENCE_LENGTH_ID;
  }

  /**
   * Scores the percentage of sentences including more than the recommended number of words.
   *
   * @param {Paper} paper The paper to use for the assessment.
   * @param {Researcher} researcher The researcher used for calling research.
   *
   * @returns {AssessmentResult} The Assessment result.
   */
  getResult({paper, researcher}) {
    const sentences = researcher.getResearch('countSentencesFromText');
    const hasDescription = paper.hasDescription();
    if (researcher.getConfig('sentenceLength')) {
      this._config = this.getLanguageSpecificConfig(researcher);
    }
    const percentage = this.calculatePercentage(sentences);

    const result = this.calculateResult(percentage, hasDescription);

    const assessmentResult = new AssessmentResult({config: this._config});

    assessmentResult.setScore(result.score);
    assessmentResult.setStatus(result.status);

    return assessmentResult;
  }

  /**
   * Checks whether the paper has text.
   *
   * @param {Paper} paper The paper to use for the assessment.
   *
   * @returns {boolean} True when there is text.
   */
  isApplicable(paper) {
    return true;
  }

  /**
   * Check if there is language-specific config, and if so, overwrite the current config with it.
   *
   * @param {Researcher} researcher The researcher to use.
   *
   * @returns {Object} The config that should be used.
   */
  getLanguageSpecificConfig(researcher) {
    const currentConfig = this._config;
    const languageSpecificConfig = researcher.getConfig('sentenceLength');

    if (languageSpecificConfig.hasOwnProperty('recommendedLength')) {
      currentConfig.recommendedLength = languageSpecificConfig.recommendedLength;
    }

    return currentConfig;
  }

  /**
   * Calculates the percentage of sentences that are too long.
   *
   * @param {Array} sentences The sentences to calculate the percentage for.
   * @returns {number} The calculates percentage of too long sentences.
   */
  calculatePercentage(sentences) {
    let percentage = 0;

    if (sentences.length !== 0) {
      const tooLongTotal = this.countTooLongSentences(sentences);
      percentage = formatNumber((tooLongTotal / sentences.length) * 100);
    }

    return percentage;
  }

  /**
   *
   * @param percentage
   * @param hasDescription
   * @returns {{score: number, status: string}}
   */
  calculateResult(percentage, hasDescription) {
    let status = 'bad';

    if (hasDescription && percentage <= this._config.slightlyTooMany) {
      status = 'good';
    }
    return {
      score: parseInt(this.getScore(MAIN_CONTENT_POINTS, status)) || 0,
      status
    };
  }

  /**
   * Gets the sentences that are qualified as being too long.
   *
   * @param {array} sentences The sentences to filter through.
   * @returns {array} Array with all the sentences considered to be too long.
   */
  getTooLongSentences(sentences) {
    return getTooLongSentences(sentences, this._config.recommendedLength);
  }

  /**
   * Get the total amount of sentences that are qualified as being too long.
   *
   * @param {Array} sentences The sentences to filter through.
   * @returns {Number} The amount of sentences that are considered too long.
   */
  countTooLongSentences(sentences) {
    return this.getTooLongSentences(sentences).length;
  }
}

export default SentenceLengthInTextAssessment;
